import { ContactType, SexType } from '@lmt/shared-types';
import i18next from 'i18next';
import * as yup from 'yup';
import { PatientData } from './types';
import { startOfDay } from 'date-fns';
import { emailRegexp } from '@lmt/unicorn-shared/app/helpers/form/react-hook-form-helper';

export const sexTypes = [SexType.Male, SexType.Female];
const minSupportedBirthDate = new Date(1900, 0, 1, 1);
export const titleKey = 'Title';
export const levelOfCareKey = 'Level of care';

function t(value: string, options?: any) {
  return i18next.t(`translation:errors.${value}` as any, options);
}

export const patientDataYupSchema: () => yup.Schema<
  Partial<PatientData>
> = () =>
  yup.object({
    values: yup.object(),
    firstName: yup
      .string()
      .trim()
      .required(() => t('required'))
      .max(55, () => t('max_length', { maxLength: 55 })),
    lastName: yup
      .string()
      .trim()
      .required(() => t('required'))
      .max(55, () => t('max_length', { maxLength: 55 })),
    sex: yup.mixed<SexType>().defined(),
    contact: yup.string().trim(),
    subtenantId: yup.number().required(() => t('required')),
    dateOfBirth: yup
      .date()
      .required(() => t('required'))
      .min(minSupportedBirthDate, ({ min }) =>
        t('must_be_later_than', {
          date: (min as Date).toDateString(),
        }),
      )
      .max(
        /* startOfToday() doesn't account for timezone*/
        startOfDay(new Date()).toDateString(),
        () => t('date_cannot_be_in_the_future'),
      )
      .typeError(() => t('invalid_date')),
    dateOfDeath: yup
      .date()
      .typeError(() => t('invalid_date'))
      .nullable()
      .max(
        /* startOfToday() doesn't account for timezone*/
        startOfDay(new Date()).toDateString(),
        () => t('date_cannot_be_in_the_future'),
      )
      .min(yup.ref('dateOfBirth'), () =>
        t('date_of_death_must_be_later_than_date_of_birth'),
      ),
    mobilePhone: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    // .matches(phoneRegexp, {
    //   excludeEmptyString: true,
    //   message: () => t('invalid'),
    // })
    homePhone: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    // .matches(phoneRegexp, {
    //   excludeEmptyString: true,
    //   message: () => t('invalid'),
    // })

    workPhone: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    // .matches(phoneRegexp, {
    //   excludeEmptyString: true,
    //   message: () => t('invalid'),
    // })
    email: yup
      .string()
      .trim()
      .max(254, () => t('max_length', { maxLength: 254 }))
      .matches(emailRegexp, {
        excludeEmptyString: true,
        message: () => t('invalid'),
      }),
    emailsAllowed: yup.boolean(),
    fax: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    // .matches(faxRegexp, {
    //   excludeEmptyString: true,
    //   message: () => t('invalid'),
    // })
    insuranceNumber: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    postcode: yup
      .string()
      .trim()
      .max(50, () => t('max_length', { maxLength: 50 })),
    address: yup
      .string()
      .trim()
      .max(100, () => t('max_length', { maxLength: 100 })),
    location: yup
      .string()
      .trim()
      .max(55, () => t('max_length', { maxLength: 55 })),
    doctorId: yup.number().nullable(),
    healthInsuranceCompanyId: yup.number().nullable(),
    isPrivateInsurance: yup.boolean(),
    isSelfPayer: yup.boolean(),
    number: yup
      .string()
      .trim()
      .test(
        'undefined-or-not-empty',
        () => t('required'),
        (value, context) =>
          !context.parent.id ||
          (typeof value === 'string' && value.trim().length > 0),
      )
      .max(20, () => t('max_length', { maxLength: 20 })),
    isDeceased: yup.boolean(),
    numberSource: yup.mixed() as any,
    priorityContactType: yup.mixed() as any,
    priorityContact: yup
      .string()
      .trim()
      .when('priorityContactType', {
        is: (contactType?: ContactType) => !!contactType,
        then: (schema) =>
          schema.required(() =>
            i18next.t(
              `translation:patients.form.errors.required_priority_contact`,
            ),
          ),
      }),
    comments: yup
      .string()
      .trim()
      .max(365, () => t('max_length', { maxLength: 365 })),
  });

export function getContactFieldValue(data: {
  email?: string | null;
  homePhone?: string | null;
  workPhone?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
}): string {
  return `${data.email || ''}${data.mobilePhone || ''}${data.homePhone || ''}${
    data.workPhone || ''
  }${data.fax || ''}`;
}

export function getFullName(
  data?: {
    firstName: string;
    lastName: string;
  } | null,
): string {
  if (!data) return '';
  return `${data.lastName || ''} ${data.firstName || ''}`;
}

export function getFullNameStartWithLast(data: {
  firstName: string;
  lastName: string;
}): string {
  return `${data.lastName || ''} ${data.firstName || ''}`;
}

export function getPriorityContactFieldValue(data: {
  email?: string | null;
  homePhone?: string | null;
  workPhone?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
  priorityContactType: ContactType;
}): string | undefined | null {
  switch (data.priorityContactType) {
    case ContactType.MobilePhone:
      return data.mobilePhone;
    case ContactType.HomePhone:
      return data.homePhone;
    case ContactType.WorkPhone:
      return data.workPhone;
    case ContactType.Fax:
      return data.fax;
    case ContactType.Email:
      return data.email;
    default:
      return '';
  }
}

export function getPriorityContactType(data: {
  email?: string | null;
  homePhone?: string | null;
  workPhone?: string | null;
  mobilePhone?: string | null;
  fax?: string | null;
}): ContactType | undefined {
  if (data.email) {
    return ContactType.Email;
  } else if (data.mobilePhone) {
    return ContactType.MobilePhone;
  } else if (data.homePhone) {
    return ContactType.HomePhone;
  } else if (data.workPhone) {
    return ContactType.WorkPhone;
  } else if (data.fax) {
    return ContactType.Fax;
  } else {
    return undefined;
  }
}
